import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { styled } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';

import FilterSelectModal from './components/FilterSelectModal';
import { IndicatorFilterT } from '../../helpers/types';
import ClearIconComponent from './components/ui/ClearIconComponent';

const StySelect = styled(Select)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    '&&& .MuiSelect-select': {
        display: 'block',
        paddingRight: '0px',
    },
}));

type GenericIndicatorFilterTypeT = 'aggregation' | 'connector' | 'chartType';

const renderIndicatorFilterValue = <T extends string>(
    selectedValues: IndicatorFilterT<T>[],
) => selectedValues.map((fltr) => fltr.name).join(', ');

type GenericIndicatorFilterProps<T extends string> = {
    filter: IndicatorFilterT<T>[];
    filterType: GenericIndicatorFilterTypeT;
    options: IndicatorFilterT<T>[];
    setFilter: (arg: IndicatorFilterT<T>[]) => void;
};

const GenericIndicatorFilter = <T extends string>({
    filter,
    filterType,
    options,
    setFilter,
}: GenericIndicatorFilterProps<T>): JSX.Element => {
    const { t } = useTranslation();
    const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const openModal = () => {
        setIsSelectModalOpen(true);
        setIsTooltipVisible(false);
    };
    const closeModal = () => setIsSelectModalOpen(false);
    const clearFilter = () => setFilter([]);
    const title = t(`indicators.filterType.${filterType}`);
    return (
        <Tooltip
            title={renderIndicatorFilterValue(filter)}
            open={isTooltipVisible}
        >
            <FormControl
                sx={{ width: '100%' }}
                size="small"
                onMouseEnter={() => setIsTooltipVisible(true)}
                onMouseLeave={() => setIsTooltipVisible(false)}
            >
                <InputLabel>{title}</InputLabel>
                <StySelect
                    className={`pendo-indicators-library-${filterType}-filter`}
                    open={isSelectModalOpen}
                    onClose={closeModal}
                    onOpen={openModal}
                    label={title}
                    value={filter}
                    renderValue={renderIndicatorFilterValue}
                    IconComponent={ClearIconComponent(
                        clearFilter,
                        filter.length > 0,
                    )}
                >
                    <FilterSelectModal
                        filter={filter}
                        options={options}
                        setFilter={setFilter}
                        title={title}
                    />
                </StySelect>
            </FormControl>
        </Tooltip>
    );
};

export default memo(GenericIndicatorFilter);
